import { ApiError, isApiError } from './errors';
import { track } from '../quiet';
import { HOME_SCOPE, type SessionScope } from './scope';
import { memorySessionStore, type SessionStore, type StoredSession } from './session-store';
import type { AccessClaims, ApiEnvelope, AuthTokens, SwitchSubaccountResponse } from './types';

export type HttpMethod = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
export type FetchLike = (input: string, init: RequestInit) => Promise<Response>;

/**
 * How the refresh token travels.
 *  - cookie: an httpOnly cookie set by the API (requests use credentials: 'include');
 *    the script never sees or stores it. What the app uses.
 *  - body: in JSON bodies, kept in the SessionStore. Deprecated; kept for one cycle.
 */
export type RefreshTransport = 'body' | 'cookie';

export interface ApiClientOptions {
  /** API worker base URL including the base path, e.g. http://localhost:8787/api/v1 */
  baseUrl: string;
  fetch?: FetchLike;
  store?: SessionStore;
  /** Default 'body'. */
  refreshTransport?: RefreshTransport;
}

/** The refresh credential for one session: the token itself, or which cookie slot holds it. */
type RefreshCredential = { kind: 'body'; token: string } | { kind: 'cookie'; slot: 'home' | 'subaccount' };

export interface RequestOptions {
  body?: unknown;
  /** A non-JSON body (e.g. a file upload), sent as-is with its content type. */
  rawBody?: { data: Blob | ArrayBuffer; contentType: string };
  query?: Record<string, string | undefined>;
  /** Send the access token and refresh on 401 (default true). */
  auth?: boolean;
  /** Extra request headers, e.g. Idempotency-Key. */
  headers?: Record<string, string>;
}

export interface ClientState {
  authenticated: boolean;
  scope: SessionScope;
  claims: AccessClaims | null;
}

type Listener = (state: ClientState) => void;

/** What to read along with the session bootstrap: the agency, and the first screen's own reads */
export interface BootstrapHint {
  agencyId?: string;
  reads?: Array<{ method: HttpMethod; path: string; query?: Record<string, string | undefined>; body?: unknown }>;
}

/** Requests that only read (a POST that is a search with a body counts) */
const READ_POSTS: ReadonlySet<string> = new Set(['/contacts/search']);

function isRead(method: HttpMethod, path: string): boolean {
  return method === 'GET' || (method === 'POST' && READ_POSTS.has(path));
}

function isEnvelope(value: unknown): value is ApiEnvelope<unknown> {
  return typeof value === 'object' && value !== null && typeof (value as { success?: unknown }).success === 'boolean';
}

function decodeBase64Url(segment: string): string {
  const base64 = segment.replace(/-/g, '+').replace(/_/g, '/');
  return atob(base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '='));
}

/**
 * Reads the claims the UI displays from an access token. The signature is NOT
 * verified (the server does that); returns null for anything that is not a
 * well-formed JWT with the expected claims.
 */
export function decodeAccessClaims(token: string): AccessClaims | null {
  const payload = token.split('.')[1];
  if (!payload) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(decodeBase64Url(payload));
  } catch {
    return null;
  }
  if (typeof parsed !== 'object' || parsed === null) return null;
  const claims = parsed as Record<string, unknown>;
  if (typeof claims.sub !== 'string' || typeof claims.tid !== 'string') return null;
  return {
    sub: claims.sub,
    tid: claims.tid,
    sid: typeof claims.sid === 'string' ? claims.sid : '',
    role: (typeof claims.role === 'string' ? claims.role : 'free_user') as AccessClaims['role'],
    exp: typeof claims.exp === 'number' ? claims.exp : 0,
  };
}

/**
 * HTTP client for the API worker.
 *
 * Tokens: the access token is held in memory only; the refresh token (and the
 * scope it belongs to) goes to the SessionStore so a reload can resume.
 * On a 401 the client refreshes once and retries. Refreshes are single-flight:
 * concurrent 401s share one POST /auth/refresh, which matters because the
 * backend rotates refresh tokens and treats reuse of an old one as theft.
 */
export class ApiClient {
  /** API base URL including the base path (no trailing slash). */
  readonly baseUrl: string;
  private readonly fetchImpl: FetchLike;
  private readonly store: SessionStore;

  private readonly transport: RefreshTransport;
  private accessToken: string | null = null;
  /** Credential of the current session (null: signed out). Compared by identity to detect replacement. */
  private refreshToken: RefreshCredential | null = null;
  /** The agency-level session, kept while working inside a sub-account. */
  private homeRefreshToken: RefreshCredential | null = null;
  private scope: SessionScope = HOME_SCOPE;
  private refreshInFlight: Promise<void> | null = null;
  /** Reads started early (route prefetch, session bootstrap), each handed to the first identical request */
  private readonly early = new Map<string, { at: number; promise: Promise<unknown> }>();
  /** Resume with POST /auth/bootstrap and keep its reads for the first screen */
  private bootstrapHint: BootstrapHint | null = null;
  private state: ClientState;
  private readonly listeners = new Set<Listener>();

  constructor(options: ApiClientOptions) {
    this.baseUrl = options.baseUrl.replace(/\/+$/, '');
    this.fetchImpl = options.fetch ?? ((input, init) => globalThis.fetch(input, init));
    this.store = options.store ?? memorySessionStore();
    this.transport = options.refreshTransport ?? 'body';

    const stored = this.store.load();
    if (stored) this.restoreFrom(stored);
    this.state = this.computeState();
  }

  private restoreFrom(stored: StoredSession): void {
    if (this.transport === 'cookie') {
      if (stored.transport !== 'cookie') {
        // A refresh token left in storage by the old body transport: drop it.
        this.store.save(null);
        return;
      }
      this.scope = stored.scope;
      this.refreshToken = this.cookie(stored.scope.kind === 'subaccount' ? 'subaccount' : 'home');
      this.homeRefreshToken = stored.homeSession ? this.cookie('home') : null;
      return;
    }
    if (stored.transport === 'cookie' || stored.refreshToken === undefined) {
      this.store.save(null);
      return;
    }
    this.refreshToken = { kind: 'body', token: stored.refreshToken };
    this.homeRefreshToken = stored.homeRefreshToken ? { kind: 'body', token: stored.homeRefreshToken } : null;
    this.scope = stored.scope;
  }

  private cookie(slot: 'home' | 'subaccount'): RefreshCredential {
    return { kind: 'cookie', slot };
  }

  /** The credential for newly issued tokens, in this client's transport. */
  private credentialFor(tokens: { refreshToken?: string }, slot: 'home' | 'subaccount'): RefreshCredential {
    if (this.transport === 'cookie') return this.cookie(slot);
    if (!tokens.refreshToken) {
      throw new ApiError('INVALID_RESPONSE', 'The server did not return a refresh token.', 200);
    }
    return { kind: 'body', token: tokens.refreshToken };
  }

  private refreshBody(credential: RefreshCredential): Record<string, string> {
    return credential.kind === 'body' ? { refreshToken: credential.token } : { session: credential.slot };
  }

  // ==================== State ====================

  getState = (): ClientState => this.state;

  subscribe = (listener: Listener): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  /** True when a stored refresh token exists but no access token has been minted yet. */
  get needsRestore(): boolean {
    return this.accessToken === null && this.refreshToken !== null;
  }

  private computeState(): ClientState {
    return {
      authenticated: this.refreshToken !== null,
      scope: this.scope,
      claims: this.accessToken ? decodeAccessClaims(this.accessToken) : null,
    };
  }

  private toStored(): StoredSession | null {
    const current = this.refreshToken;
    if (!current) return null;
    if (current.kind === 'cookie') {
      return { transport: 'cookie', scope: this.scope, ...(this.homeRefreshToken ? { homeSession: true } : {}) };
    }
    const home = this.homeRefreshToken;
    return {
      refreshToken: current.token,
      scope: this.scope,
      ...(home?.kind === 'body' ? { homeRefreshToken: home.token } : {}),
    };
  }

  private changed(): void {
    this.store.save(this.toStored());
    this.state = this.computeState();
    for (const listener of this.listeners) listener(this.state);
  }

  /** Installs a fresh agency-level session (after login or register). */
  setSession(tokens: AuthTokens): void {
    this.early.clear();
    const credential = this.credentialFor(tokens, 'home');
    this.accessToken = tokens.accessToken;
    this.refreshToken = credential;
    this.homeRefreshToken = null;
    this.scope = HOME_SCOPE;
    this.changed();
  }

  /** Forgets every token locally. Does not contact the server. */
  clearSession(): void {
    this.early.clear();
    this.accessToken = null;
    this.refreshToken = null;
    this.homeRefreshToken = null;
    this.scope = HOME_SCOPE;
    this.changed();
  }

  // ==================== Requests ====================

  async request<T>(method: HttpMethod, path: string, options: RequestOptions = {}): Promise<T> {
    if (!isRead(method, path)) {
      // A change may make any early read stale
      this.early.clear();
      return this.requestNow<T>(method, path, options);
    }
    const early = this.takeEarly(this.earlyKey(method, path, options));
    // An early read that failed is simply asked again, so the screen sees the real answer
    if (early) return early.then((data) => data as T, () => this.requestNow<T>(method, path, options));
    return this.requestNow<T>(method, path, options);
  }

  private async requestNow<T>(method: HttpMethod, path: string, options: RequestOptions): Promise<T> {
    return this.parse<T>(await this.sendAuthorized(method, path, options));
  }

  // ==================== Early reads (docs/ux/plan.md, B7 and B8) ====================

  /** How long an early read waits for the screen that asks for it */
  static readonly EARLY_TTL_MS = 15_000;

  private earlyKey(method: HttpMethod, path: string, options: RequestOptions): string {
    return `${method} ${this.buildUrl(path, options.query)} ${options.body === undefined ? '' : JSON.stringify(options.body)}`;
  }

  private takeEarly(key: string): Promise<unknown> | null {
    const entry = this.early.get(key);
    if (!entry) return null;
    this.early.delete(key);
    return Date.now() - entry.at <= ApiClient.EARLY_TTL_MS ? entry.promise : null;
  }

  /**
   * Start a read now (a screen that is about to open asks for this): the first identical
   * request within EARLY_TTL_MS gets this answer instead of asking again. Any write, sign-in,
   * sign-out or change of sub-account drops what was read early. Reads only.
   */
  prefetch<T>(method: HttpMethod, path: string, options: RequestOptions = {}): Promise<T> {
    if (!isRead(method, path)) return Promise.reject(new TypeError(`Only reads can be prefetched (${method} ${path})`));
    const key = this.earlyKey(method, path, options);
    const existing = this.early.get(key);
    if (existing && Date.now() - existing.at <= ApiClient.EARLY_TTL_MS) return existing.promise as Promise<T>;
    const promise = this.requestNow<T>(method, path, options);
    this.early.set(key, { at: Date.now(), promise });
    return promise;
  }

  /** A read started early that no request has taken yet (null if there is none): look, don't take */
  peekEarly<T>(method: HttpMethod, path: string, options: RequestOptions = {}): Promise<T> | null {
    const entry = this.early.get(this.earlyKey(method, path, options));
    return entry && Date.now() - entry.at <= ApiClient.EARLY_TTL_MS ? (entry.promise as Promise<T>) : null;
  }

  /**
   * Resume a stored cookie session now, with POST /auth/bootstrap: one call that also reads
   * the agency's first data (kept for the screens' own requests). Joins a refresh in flight.
   */
  resume(hint: BootstrapHint): Promise<void> {
    if (!this.refreshInFlight) this.bootstrapHint = hint;
    return this.refresh();
  }

  /**
   * Like request(), for endpoints that answer with a file (e.g. text/csv)
   * instead of the JSON envelope. Errors still come back as envelopes.
   */
  async requestText(method: HttpMethod, path: string, options: RequestOptions = {}): Promise<{ text: string; headers: Headers }> {
    const response = await this.sendAuthorized(method, path, options);
    if (!response.ok) throw await this.toError(response);
    return { text: await response.text(), headers: response.headers };
  }

  /** Like requestText(), for binary files (a PDF, an image). Errors still come back as envelopes. */
  async requestBlob(method: HttpMethod, path: string, options: RequestOptions = {}): Promise<{ blob: Blob; headers: Headers }> {
    const response = await this.sendAuthorized(method, path, options);
    if (!response.ok) throw await this.toError(response);
    return { blob: await response.blob(), headers: response.headers };
  }

  /** Sends with the access token, refreshing once on a 401. */
  private async sendAuthorized(method: HttpMethod, path: string, options: RequestOptions): Promise<Response> {
    const auth = options.auth ?? true;
    if (auth && this.needsRestore) await this.refresh();

    const tokenUsed = auth ? this.accessToken : null;
    let response = await this.send(method, path, options, tokenUsed);

    if (response.status === 401 && auth && this.refreshToken) {
      // Another request may already have refreshed while this one was in flight.
      if (this.accessToken === tokenUsed) await this.refresh();
      response = await this.send(method, path, options, this.accessToken);
      if (response.status === 401) {
        const error = await this.toError(response);
        this.clearSession();
        throw error;
      }
    }
    return response;
  }

  /** Mints a new access token from the refresh token. Concurrent callers share one request. */
  refresh(): Promise<void> {
    if (!this.refreshInFlight) {
      this.refreshInFlight = this.performRefresh().finally(() => {
        this.refreshInFlight = null;
      });
    }
    return this.refreshInFlight;
  }

  private async performRefresh(): Promise<void> {
    const refreshToken = this.refreshToken;
    if (!refreshToken) throw new ApiError('UNAUTHORIZED', 'You are not signed in.', 401);

    let tokens: AuthTokens;
    try {
      const hint = this.bootstrapHint;
      this.bootstrapHint = null;
      const booted = hint && this.accessToken === null && refreshToken.kind === 'cookie' ? await this.bootstrap(refreshToken, hint) : null;
      if (booted) tokens = booted;
      else {
        const response = await this.send('POST', '/auth/refresh', { body: this.refreshBody(refreshToken) }, null);
        tokens = await this.parse<AuthTokens>(response);
      }
    } catch (err) {
      if (isApiError(err) && (err.status === 401 || err.status === 400)) {
        if (this.refreshToken === refreshToken) this.clearSession();
        throw new ApiError('SESSION_EXPIRED', 'Your session has ended. Please sign in again.', 401, { cause: err });
      }
      throw err;
    }

    // The session was replaced (logout or switch) while refreshing: these tokens are stale.
    if (this.refreshToken !== refreshToken) return;
    this.accessToken = tokens.accessToken;
    // Cookie sessions keep their slot (the server rotated the cookie); body sessions take the new token.
    if (refreshToken.kind === 'body') this.refreshToken = this.credentialFor(tokens, 'home');
    this.changed();
  }

  /**
   * POST /auth/bootstrap: the refresh plus the reads every signed-in screen starts with
   * (branding, the agency, its sub-accounts, the sub-account's plan), in one round trip.
   * Those reads are kept for the screens' own identical requests (a part that failed is asked
   * again by its screen). Returns null when the API has no bootstrap (an older deploy): the
   * caller then refreshes as before.
   */
  private async bootstrap(credential: RefreshCredential & { kind: 'cookie' }, hint: BootstrapHint): Promise<AuthTokens | null> {
    type Part = 'branding' | 'agency' | 'subaccounts' | 'plan' | number;
    const parts: Array<{ part: Part; key: string }> = [{ part: 'branding', key: this.earlyKey('GET', '/branding', {}) }];
    if (hint.agencyId) {
      const agency = `/agencies/${encodeURIComponent(hint.agencyId)}`;
      parts.push({ part: 'agency', key: this.earlyKey('GET', agency, {}) }, { part: 'subaccounts', key: this.earlyKey('GET', `${agency}/subaccounts`, {}) });
    }
    if (credential.slot === 'subaccount') parts.push({ part: 'plan', key: this.earlyKey('GET', '/billing/subaccount', {}) });
    // The first screen's own reads (route-prefetch.ts), answered in order as `reads`
    const reads = (hint.reads ?? []).filter((r) => isRead(r.method, r.path)).slice(0, 6);
    reads.forEach((r, index) => parts.push({ part: index, key: this.earlyKey(r.method, r.path, r) }));
    const waiting = parts.map(({ part, key }) => {
      let settle: { resolve: (data: unknown) => void; reject: (error: unknown) => void } | null = null;
      const promise = new Promise<unknown>((resolve, reject) => {
        settle = { resolve, reject };
      });
      // The screen that asks for this part asks again on its own
      promise.catch((error: unknown) => console.debug(`[api] bootstrap part ${part} failed; its screen asks again`, error));
      this.early.set(key, { at: Date.now(), promise });
      return { part, key, promise, settle: settle! as { resolve: (data: unknown) => void; reject: (error: unknown) => void } };
    });
    const drop = (reason: unknown) => {
      for (const w of waiting) {
        if (this.early.get(w.key)?.promise === w.promise) this.early.delete(w.key);
        w.settle.reject(reason);
      }
    };

    let response: Response;
    try {
      const body = {
        session: credential.slot,
        ...(hint.agencyId ? { agencyId: hint.agencyId } : {}),
        ...(reads.length ? { reads: reads.map((r) => ({ method: r.method, path: this.buildUrl(r.path, r.query).slice(this.baseUrl.length), ...(r.body !== undefined ? { body: r.body } : {}) })) } : {}),
      };
      response = await this.send('POST', '/auth/bootstrap', { body }, null);
    } catch (err) {
      drop(err);
      throw err;
    }
    if (response.status === 404 || response.status === 405) {
      drop(new ApiError('NOT_FOUND', 'No session bootstrap on this API', response.status));
      return null;
    }
    type Answer = { status: number; data?: unknown; error?: unknown };
    let data: { tokens: AuthTokens; reads?: Answer[] } & Partial<Record<'branding' | 'agency' | 'subaccounts' | 'plan', Answer>>;
    try {
      data = await this.parse<typeof data>(response);
    } catch (err) {
      drop(err);
      throw err;
    }
    for (const w of waiting) {
      const part = typeof w.part === 'number' ? data.reads?.[w.part] : data[w.part];
      if (part && part.status >= 200 && part.status < 300 && 'data' in part) w.settle.resolve(part.data);
      else w.settle.reject(new ApiError('BOOTSTRAP_PART_FAILED', `The session bootstrap could not read ${w.part}.`, part?.status ?? 0, { details: typeof part?.error === 'object' && part.error !== null ? (part.error as Record<string, unknown>) : undefined }));
    }
    return data.tokens;
  }

  private buildUrl(path: string, query?: Record<string, string | undefined>): string {
    const url = `${this.baseUrl}${path.startsWith('/') ? path : `/${path}`}`;
    if (!query) return url;
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(query)) {
      if (value !== undefined) params.set(key, value);
    }
    const search = params.toString();
    return search ? `${url}?${search}` : url;
  }

  private async send(
    method: HttpMethod,
    path: string,
    options: RequestOptions,
    accessToken: string | null
  ): Promise<Response> {
    const headers: Record<string, string> = { ...options.headers, Accept: 'application/json' };
    if (options.rawBody) headers['Content-Type'] = options.rawBody.contentType;
    else if (options.body !== undefined) headers['Content-Type'] = 'application/json';
    if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
    try {
      return await track(this.fetchImpl(this.buildUrl(path, options.query), {
        method,
        headers,
        body: options.rawBody ? options.rawBody.data : options.body === undefined ? undefined : JSON.stringify(options.body),
        // The refresh cookie only travels (and is only stored) on credentialed requests.
        ...(this.transport === 'cookie' ? { credentials: 'include' as const } : {}),
      }));
    } catch (err) {
      throw new ApiError('NETWORK_ERROR', 'Could not reach the server. Check your connection and try again.', 0, {
        cause: err,
      });
    }
  }

  private async readEnvelope(response: Response): Promise<unknown> {
    const text = await response.text();
    if (!text) return null;
    try {
      return JSON.parse(text) as unknown;
    } catch (err) {
      throw new ApiError('INVALID_RESPONSE', `Unexpected response from the server (HTTP ${response.status}).`, response.status, {
        cause: err,
      });
    }
  }

  private async toError(response: Response): Promise<ApiError> {
    try {
      await this.parse<unknown>(response);
    } catch (err) {
      if (isApiError(err)) return err;
      throw err;
    }
    return new ApiError(`HTTP_${response.status}`, `Request failed (HTTP ${response.status}).`, response.status);
  }

  private async parse<T>(response: Response): Promise<T> {
    const payload = await this.readEnvelope(response);
    if (isEnvelope(payload)) {
      if (payload.success && response.ok) return payload.data as T;
      if (!payload.success) {
        throw new ApiError(payload.error.code, payload.error.message, response.status, {
          details: payload.error.details,
          requestId: payload.meta?.requestId,
        });
      }
    }
    if (!response.ok) {
      throw new ApiError(`HTTP_${response.status}`, `Request failed (HTTP ${response.status}).`, response.status);
    }
    throw new ApiError('INVALID_RESPONSE', 'Unexpected response from the server.', response.status);
  }

  /** Ends one server session, identified by its access token. A 401 means it is already gone. */
  private async revoke(accessToken: string): Promise<void> {
    const response = await this.send('POST', '/auth/logout', {}, accessToken);
    if (response.status === 401) return;
    await this.parse<unknown>(response);
  }

  // ==================== Scope ====================

  /**
   * Re-scopes the client to a sub-account. Subsequent requests carry the
   * sub-account tokens; the agency session is kept for exitSubaccount().
   * Switching between sub-accounts ends the one being left.
   */
  async switchSubaccount(agencyId: string, subaccountId: string): Promise<SwitchSubaccountResponse> {
    const result = await this.request<SwitchSubaccountResponse>(
      'POST',
      `/agencies/${encodeURIComponent(agencyId)}/subaccounts/${encodeURIComponent(subaccountId)}/switch`
    );
    const leaving = this.scope.kind === 'subaccount' ? this.accessToken : null;
    if (this.scope.kind === 'home') this.homeRefreshToken = this.refreshToken;

    this.early.clear();
    this.accessToken = result.tokens.accessToken;
    this.refreshToken = this.credentialFor(result.tokens, 'subaccount');
    this.scope = { kind: 'subaccount', agencyId, subaccount: result.subaccount, role: result.role };
    this.changed();

    if (leaving) {
      try {
        await this.revoke(leaving);
      } catch (err) {
        console.warn('[api] could not end the previous sub-account session; it will expire on its own', err);
      }
    }
    return result;
  }

  /** Returns to the agency-level session, ending the sub-account session. */
  async exitSubaccount(): Promise<void> {
    if (this.scope.kind !== 'subaccount') return;
    const leaving = this.accessToken;
    const home = this.homeRefreshToken;

    this.early.clear();
    this.accessToken = null;
    this.refreshToken = home;
    this.homeRefreshToken = null;
    this.scope = HOME_SCOPE;
    this.changed();

    if (leaving) {
      try {
        await this.revoke(leaving);
      } catch (err) {
        console.warn('[api] could not end the sub-account session; it will expire on its own', err);
      }
    }
    if (!home) throw new ApiError('SESSION_EXPIRED', 'Your agency session has ended. Please sign in again.', 401);
    await this.refresh();
  }

  /**
   * Signs out: clears local state first (so sign-out always works on this
   * device), then ends the server sessions. Throws if the server could not
   * confirm, after local state is already cleared.
   */
  async logout(): Promise<void> {
    const access = this.accessToken;
    const refresh = this.refreshToken;
    const home = this.homeRefreshToken;
    this.clearSession();

    const failures: unknown[] = [];
    const end = async (accessToken: string | null, refreshToken: RefreshCredential | null) => {
      try {
        let token = accessToken;
        if (!token && refreshToken) {
          const response = await this.send('POST', '/auth/refresh', { body: this.refreshBody(refreshToken) }, null);
          if (response.status === 401) return;
          token = (await this.parse<AuthTokens>(response)).accessToken;
        }
        if (token) await this.revoke(token);
      } catch (err) {
        failures.push(err);
      }
    };
    await end(access, refresh);
    if (home) await end(null, home);

    if (failures.length > 0) {
      throw new ApiError('LOGOUT_INCOMPLETE', 'Signed out on this device, but the server could not confirm.', 0, {
        cause: failures.length === 1 ? failures[0] : new AggregateError(failures),
      });
    }
  }
}
